/**
 * Announcements: what the admin team tells everyone, brands or creators. Data and actions for
 * the admin page. An announcement shows among its readers' notifications.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.js";
import { NOTIFICATIONS_QUERY_KEY } from "../notifications/notifications.js";

/** Who an announcement is for. */
export type AnnouncementAudience = "everyone" | "brands" | "creators";

/** An announcement, as `GET /api/admin/announcements` describes it. */
export interface Announcement {
  id: number;
  audience: AnnouncementAudience;
  title: string;
  body: string;
  link: string | null;
  /** ISO 8601 time it was published. */
  createdAt: string;
  /** The admin wallet that published it. */
  createdBy: string | null;
  /** ISO 8601 time it was taken back. Null while it is shown. */
  withdrawnAt: string | null;
}

/** What an admin enters to publish one. */
export interface AnnouncementInput {
  audience: AnnouncementAudience;
  title: string;
  body: string;
  link?: string;
}

/** The limits the API checks, which the form checks first. */
export const ANNOUNCEMENT_LIMITS = { title: [3, 80], body: [10, 600], link: 300 } as const;

const ANNOUNCEMENTS_QUERY_KEY = ["admin", "announcements"] as const;

/** The announcements, the newest first, with those taken back. */
export function useAnnouncements() {
  return useQuery({
    queryKey: ANNOUNCEMENTS_QUERY_KEY,
    queryFn: async () =>
      (await api.get<{ announcements: Announcement[] }>("/api/admin/announcements")).announcements,
  });
}

/** An admin action on announcements. The lists that show them are read again afterwards. */
function useAnnouncementAction<Input>(action: (input: Input) => Promise<Announcement>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY }),
        // The admin is one of an announcement's readers.
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
      ]),
  });
}

/** Publishes an announcement to its audience. */
export function usePublishAnnouncement() {
  return useAnnouncementAction((input: AnnouncementInput) =>
    api.post<Announcement>("/api/admin/announcements", input),
  );
}

/** Takes an announcement back, so that nobody sees it any more. */
export function useWithdrawAnnouncement() {
  return useAnnouncementAction((id: number) =>
    api.post<Announcement>(`/api/admin/announcements/${id}/withdraw`),
  );
}
